import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { ListSkeleton } from '../components/ui';
import { RUOLI, ruoloLabel } from '../lib/ruoli';
import {
  Users, Mail, Copy, Check, Trash2, Plus, Link2,
  ShieldCheck, UserCheck, Bot, Clock, X, ToggleLeft, ToggleRight,
  Activity, AlertTriangle, TrendingUp, Filter, RefreshCw, ChevronLeft, ChevronRight,
  Terminal, Database, Upload, Calendar, LogIn, UserPlus, Edit3
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────
const RUOLO_STYLE = {
  admin:     { bg:'var(--cyan-dim)',    color:'var(--cyan)',    icon:ShieldCheck },
  operatore: { bg:'var(--green-dim)',   color:'var(--green)',   icon:UserCheck   },
  agente:    { bg:'var(--purple-dim)',  color:'var(--purple)',  icon:Bot         },
  direttore: { bg:'var(--yellow-dim)', color:'var(--yellow)',  icon:UserCheck   },
  social_manager: { bg:'rgba(236,72,153,0.12)', color:'#ec4899', icon:UserCheck },
  videomaker:     { bg:'rgba(249,115,22,0.12)', color:'#f97316', icon:UserCheck },
};

const AZIONE_ICON = {
  LOGIN:            { icon:LogIn,     color:'var(--cyan)'   },
  CREA_UTENTE:      { icon:UserPlus,  color:'var(--green)'  },
  REGISTRA_INVITO:  { icon:UserPlus,  color:'var(--green)'  },
  CREA_INVITO:      { icon:Mail,      color:'var(--cyan)'   },
  REVOCA_INVITO:    { icon:X,         color:'var(--red)'    },
  TOGGLE_UTENTE:    { icon:ToggleRight,color:'var(--yellow)'},
  CAMBIA_RUOLO:     { icon:ShieldCheck,color:'var(--cyan)'  },
  ELIMINA_UTENTE:   { icon:Trash2,    color:'var(--red)'    },
  CREA_CLIENTE:     { icon:UserPlus,  color:'var(--green)'  },
  MODIFICA_CLIENTE: { icon:Edit3,     color:'var(--yellow)' },
  ELIMINA_CLIENTE:  { icon:Trash2,    color:'var(--red)'    },
  CREA_TASK:        { icon:Plus,      color:'var(--green)'  },
  MODIFICA_TASK:    { icon:Edit3,     color:'var(--yellow)' },
  ELIMINA_TASK:     { icon:Trash2,    color:'var(--red)'    },
  CREA_EVENTO:      { icon:Calendar,  color:'var(--purple)' },
  MODIFICA_EVENTO:  { icon:Edit3,     color:'var(--yellow)' },
  ELIMINA_EVENTO:   { icon:Trash2,    color:'var(--red)'    },
  UPLOAD_FILE:      { icon:Upload,    color:'var(--cyan)'   },
  ELIMINA_FILE:     { icon:Trash2,    color:'var(--red)'    },
  MCP_EXECUTE_QUERY:{ icon:Terminal,  color:'var(--purple)' },
  MCP_LIST_TABLES:  { icon:Database,  color:'var(--cyan)'   },
  MCP_GET_DB_SCHEMA:{ icon:Database,  color:'var(--cyan)'   },
  MCP_WRITE_LOG:    { icon:Activity,  color:'var(--green)'  },
};

// Determina se un log è esterno al CRM (agente che logga attività proprie)
function isLogEsterno(log) {
  return log.ruolo === 'agente' && log.path && log.path.includes('write_log');
}

// Mappa azione → frase leggibile (senza soggetto, aggiunto dopo)
const AZIONE_FRASE = {
  LOGIN:             (d) => 'ha effettuato il login',
  CREA_UTENTE:       (d) => 'ha creato un account',
  REGISTRA_INVITO:   (d) => 'si è registrato tramite invito',
  CREA_INVITO:       (d) => d ? `ha invitato ${d}` : 'ha creato un invito',
  REVOCA_INVITO:     (d) => 'ha revocato un invito',
  TOGGLE_UTENTE:     (d) => 'ha cambiato lo stato di un utente',
  CAMBIA_RUOLO:      (d) => 'ha cambiato il ruolo di un utente',
  ELIMINA_UTENTE:    (d) => 'ha eliminato un utente',
  CREA_CLIENTE:      (d) => d ? `ha aggiunto il cliente "${d}"` : 'ha aggiunto un cliente',
  MODIFICA_CLIENTE:  (d) => d ? `ha modificato il cliente "${d}"` : 'ha modificato un cliente',
  ELIMINA_CLIENTE:   (d) => d ? `ha eliminato il cliente "${d}"` : 'ha eliminato un cliente',
  CREA_TASK:         (d) => d ? `ha creato la task "${d}"` : 'ha creato una task',
  MODIFICA_TASK:     (d) => d ? `ha modificato la task "${d}"` : 'ha modificato una task',
  ELIMINA_TASK:      (d) => 'ha eliminato una task',
  CREA_EVENTO:       (d) => d ? `ha aggiunto l'evento "${d}"` : 'ha aggiunto un evento',
  MODIFICA_EVENTO:   (d) => d ? `ha modificato l'evento "${d}"` : 'ha modificato un evento',
  ELIMINA_EVENTO:    (d) => 'ha eliminato un evento',
  UPLOAD_FILE:       (d) => d ? `ha caricato il file "${d}"` : 'ha caricato un file',
  ELIMINA_FILE:      (d) => 'ha eliminato un file',
  MCP_EXECUTE_QUERY: (d) => d ? `ha eseguito una query SQL` : 'ha eseguito una query',
  MCP_LIST_TABLES:   (d) => 'ha consultato la lista delle tabelle',
  MCP_GET_DB_SCHEMA: (d) => 'ha consultato lo schema del database',
  MCP_WRITE_LOG:     (d) => d ? `ha registrato l'azione "${d}"` : 'ha registrato un\'azione',
};

function logFrase(log) {
  const soggetto = log.utente_nome
    ? log.utente_nome.trim()
    : log.utente_email
      ? log.utente_email.split('@')[0]
      : log.ruolo === 'agente' ? 'Agente' : 'Sistema';
  const dettaglio = log.payload?.dettaglio || '';
  const fn = AZIONE_FRASE[log.azione];
  const azione = fn ? fn(dettaglio) : (log.azione || '').toLowerCase().replace(/_/g, ' ');
  return { soggetto, azione };
}

// Estrae UUID dal path e costruisce il link di navigazione
function getLogLink(log) {
  const UUID_RE = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
  const m = (log.path || '').match(UUID_RE);
  if (!m) return null;
  const id = m[1];
  const path = log.path || '';
  if (path.includes('/task')) return `/task?open=${id}`;
  if (path.includes('/clienti')) return `/clienti?open=${id}`;
  return null;
}

function copyToClipboard(text, setCopied) {
  navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(()=>setCopied(false),2000); });
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={()=>copyToClipboard(text,setCopied)} style={{
      background:copied?'var(--green-dim)':'var(--surface)',
      border:`1px solid ${copied?'rgba(16,185,129,0.3)':'var(--border)'}`,
      borderRadius:6,color:copied?'var(--green)':'var(--text-muted)',
      cursor:'pointer',padding:'4px 10px',display:'flex',alignItems:'center',gap:5,
      fontSize:12,flexShrink:0,transition:'all 0.2s',whiteSpace:'nowrap',
    }}>
      {copied?<><Check size={12}/>Copiato</>:<><Copy size={12}/>Copia link</>}
    </button>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',backdropFilter:'blur(4px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:24}}>
      <div className="eden-card fade-up" style={{width:'100%',maxWidth:440,boxShadow:'var(--shadow-lg)'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'18px 24px',borderBottom:'1px solid var(--border)'}}>
          <span style={{fontSize:14,fontWeight:600,color:'var(--text)'}}>{title}</span>
          <button onClick={onClose} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',display:'flex',borderRadius:6,padding:4}}><X size={16}/></button>
        </div>
        <div style={{padding:'20px 24px'}}>{children}</div>
      </div>
    </div>
  );
}

// ── SEZIONE UTENTI ────────────────────────────────────────────────────────────
function TabUtenti({ currentUser }) {
  const [utenti, setUtenti] = useState([]);
  const load = async () => { const r = await api.get('/admin/utenti'); setUtenti(r.data); };
  useEffect(()=>{ load(); },[]);

  const toggleAttivo = async uid => { await api.patch(`/admin/utenti/${uid}/attivo`); load(); };
  const deleteUtente = async (uid,email) => {
    if(!window.confirm(`Eliminare ${email}?`)) return;
    await api.delete(`/admin/utenti/${uid}`); load();
  };
  const changeRuolo = async (uid,ruolo) => { await api.patch(`/admin/utenti/${uid}/ruolo`,{ruolo}); load(); };

  return (
    <div className="eden-card" style={{overflow:'hidden'}}>
      <div style={{overflowX:'auto'}}>
      <table className="eden-table-stack" style={{width:'100%',borderCollapse:'collapse'}}>
        <thead>
          <tr style={{borderBottom:'1px solid var(--border)'}}>
            {['Utente','Email','Ruolo','Stato','Azioni'].map(h=>(
              <th key={h} style={{textAlign:'left',padding:'12px 16px',fontSize:11.5,fontWeight:600,color:'var(--text-muted)',letterSpacing:'0.04em',textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {utenti.map((u,i)=>{
            const R=RUOLO_STYLE[u.ruolo]||RUOLO_STYLE.operatore;
            const isSelf=u.id===currentUser.id;
            return (
              <tr key={u.id} style={{borderBottom:i<utenti.length-1?'1px solid var(--border)':'none',transition:'background 0.12s'}}
                onMouseEnter={e=>e.currentTarget.style.background='var(--surface)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={{padding:'12px 16px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:32,height:32,borderRadius:'50%',background:R.bg,border:`1px solid ${R.color}44`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      <R.icon size={14} color={R.color}/>
                    </div>
                    <div>
                      <p style={{fontSize:13.5,fontWeight:500,color:'var(--text)'}}>{u.nome} {u.cognome}</p>
                      {isSelf&&<span style={{fontSize:10.5,color:'var(--text-dim)'}}>tu</span>}
                    </div>
                  </div>
                </td>
                <td className="td-meta" style={{padding:'12px 16px',fontSize:13,color:'var(--text-muted)'}}>{u.email}</td>
                <td style={{padding:'12px 16px'}}>
                  {isSelf?(
                    <span style={{fontSize:12,padding:'3px 10px',borderRadius:999,background:R.bg,color:R.color}}>{u.ruolo}</span>
                  ):(
                    <select value={u.ruolo} onChange={e=>changeRuolo(u.id,e.target.value)} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:6,color:'var(--text)',font:'inherit',fontSize:12.5,padding:'4px 8px',cursor:'pointer'}}>
                      {RUOLI.map(r => <option key={r} value={r}>{ruoloLabel(r)}</option>)}
                    </select>
                  )}
                </td>
                <td style={{padding:'12px 16px'}}>
                  <button onClick={()=>!isSelf&&toggleAttivo(u.id)} disabled={isSelf} style={{background:'none',border:'none',cursor:isSelf?'not-allowed':'pointer',display:'flex',alignItems:'center',gap:6,padding:0,opacity:isSelf?0.4:1}}>
                    {u.attivo
                      ?<><ToggleRight size={20} color="var(--green)"/><span style={{fontSize:12,color:'var(--green)'}}>Attivo</span></>
                      :<><ToggleLeft size={20} color="var(--text-dim)"/><span style={{fontSize:12,color:'var(--text-dim)'}}>Disattivato</span></>
                    }
                  </button>
                </td>
                <td style={{padding:'12px 16px'}}>
                  {!isSelf&&(
                    <button onClick={()=>deleteUtente(u.id,u.email)} style={{background:'none',border:'none',color:'var(--text-dim)',cursor:'pointer',padding:5,display:'flex',borderRadius:5}}
                      onMouseEnter={e=>e.currentTarget.style.color='var(--red)'}
                      onMouseLeave={e=>e.currentTarget.style.color='var(--text-dim)'}>
                      <Trash2 size={14}/>
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>{/* end scroll */}
    </div>
  );
}

// ── SEZIONE INVITI ────────────────────────────────────────────────────────────
function TabInviti() {
  const [inviti, setInviti] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({email:'',ruolo:'operatore',giorni_validita:7});
  const [createdLink, setCreatedLink] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => { const r=await api.get('/admin/inviti'); setInviti(r.data); };
  useEffect(()=>{ load(); },[]);

  const createInvito = async e => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await api.post('/admin/inviti',form);
      const link=`${window.location.origin}/register?token=${res.data.token}`;
      setCreatedLink({link,email:form.email,ruolo:form.ruolo});
      setShowForm(false);
      setForm({email:'',ruolo:'operatore',giorni_validita:7});
      load();
    } finally { setLoading(false); }
  };

  const revoca = async token => {
    if(!window.confirm('Revocare questo invito?')) return;
    await api.delete(`/admin/inviti/${token}`); load();
  };

  return (
    <>
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
        <button className="eden-btn-primary" onClick={()=>setShowForm(true)}><Plus size={15}/>Nuovo invito</button>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {inviti.length===0&&(
          <div className="eden-card" style={{padding:'40px',textAlign:'center'}}>
            <p style={{color:'var(--text-dim)',fontSize:13}}>Nessun invito. Crea il primo cliccando "Nuovo invito".</p>
          </div>
        )}
        {inviti.map(inv=>{
          const expired=new Date(inv.scadenza)<new Date(), used=!!inv.usato_il;
          const link=`${window.location.origin}/register?token=${inv.token}`;
          const R=RUOLO_STYLE[inv.ruolo]||RUOLO_STYLE.operatore;
          return (
            <div key={inv.id} className="eden-card" style={{padding:'16px 20px',opacity:(expired||used)?0.6:1,borderLeft:`3px solid ${used?'var(--green)':expired?'var(--red)':'var(--cyan)'}`}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                    <Mail size={14} color="var(--text-dim)"/>
                    <span style={{fontSize:13.5,fontWeight:500,color:'var(--text)'}}>{inv.email}</span>
                    <span style={{fontSize:11,padding:'2px 8px',borderRadius:999,background:R.bg,color:R.color}}>{inv.ruolo}</span>
                    {used&&<span style={{fontSize:11,padding:'2px 8px',borderRadius:999,background:'var(--green-dim)',color:'var(--green)'}}>Utilizzato</span>}
                    {expired&&!used&&<span style={{fontSize:11,padding:'2px 8px',borderRadius:999,background:'var(--red-dim)',color:'var(--red)'}}>Scaduto</span>}
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:10,fontSize:11.5,color:'var(--text-dim)'}}>
                    <span>Da {inv.creato_da_nome||'—'}</span>
                    <span>·</span><Clock size={11}/>
                    <span>Scade {new Date(inv.scadenza).toLocaleDateString('it-IT')}</span>
                  </div>
                  {!used&&!expired&&(
                    <div style={{marginTop:10,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:7,padding:'8px 12px',display:'flex',alignItems:'center',gap:8}}>
                      <Link2 size={12} color="var(--text-dim)" style={{flexShrink:0}}/>
                      <span style={{fontSize:11.5,color:'var(--text-muted)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{link}</span>
                      <CopyBtn text={link}/>
                    </div>
                  )}
                </div>
                {!used&&(
                  <button onClick={()=>revoca(inv.token)} style={{background:'none',border:'none',color:'var(--text-dim)',cursor:'pointer',padding:5,display:'flex',borderRadius:5,flexShrink:0}}
                    onMouseEnter={e=>e.currentTarget.style.color='var(--red)'}
                    onMouseLeave={e=>e.currentTarget.style.color='var(--text-dim)'}>
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showForm&&(
        <Modal title="Nuovo invito" onClose={()=>setShowForm(false)}>
          <form onSubmit={createInvito} style={{display:'flex',flexDirection:'column',gap:14}}>
            <div>
              <label style={{fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em',display:'block',marginBottom:5}}>Email destinatario *</label>
              <input type="email" className="eden-input" value={form.email} required onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="mario.rossi@example.com"/>
              <p style={{fontSize:11,color:'var(--text-dim)',marginTop:4}}>L'utente non potrà modificare questa email durante la registrazione.</p>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em',display:'block',marginBottom:5}}>Ruolo</label>
              <select className="eden-select" value={form.ruolo} onChange={e=>setForm(f=>({...f,ruolo:e.target.value}))} style={{width:'100%'}}>
                {RUOLI.map(r => <option key={r} value={r}>{ruoloLabel(r)}</option>)}
              </select>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em',display:'block',marginBottom:5}}>Validità</label>
              <select className="eden-select" value={form.giorni_validita} onChange={e=>setForm(f=>({...f,giorni_validita:Number(e.target.value)}))} style={{width:'100%'}}>
                {[1,3,7,14,30].map(d=><option key={d} value={d}>{d} {d===1?'giorno':'giorni'}</option>)}
              </select>
            </div>
            <div style={{background:'var(--cyan-dim)',border:'1px solid rgba(0,212,255,0.2)',borderRadius:8,padding:'10px 12px',fontSize:12,color:'var(--cyan)'}}>
              Il link è monouso e scade automaticamente.
            </div>
            <div style={{display:'flex',gap:10}}>
              <button type="submit" disabled={loading} className="eden-btn-primary" style={{flex:1,justifyContent:'center'}}>
                {loading?'Creazione...':'Genera link invito'}
              </button>
              <button type="button" onClick={()=>setShowForm(false)} className="eden-btn-ghost">Annulla</button>
            </div>
          </form>
        </Modal>
      )}

      {createdLink&&(
        <Modal title="Link invito generato" onClose={()=>setCreatedLink(null)}>
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <div style={{background:'var(--green-dim)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:8,padding:'12px 16px'}}>
              <p style={{fontSize:12.5,color:'var(--green)',fontWeight:600,marginBottom:2}}>Invito creato</p>
              <p style={{fontSize:12,color:'var(--green)',opacity:0.8}}>{createdLink.email} · {createdLink.ruolo}</p>
            </div>
            <div>
              <p style={{fontSize:11.5,color:'var(--text-muted)',marginBottom:8}}>Condividi questo link:</p>
              <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 14px',display:'flex',alignItems:'center',gap:10}}>
                <span style={{fontSize:12,color:'var(--text)',flex:1,wordBreak:'break-all',lineHeight:1.5}}>{createdLink.link}</span>
                <CopyBtn text={createdLink.link}/>
              </div>
            </div>
            <p style={{fontSize:11.5,color:'var(--text-dim)',lineHeight:1.6}}>Email pre-compilata e bloccata. Link monouso.</p>
            <button onClick={()=>setCreatedLink(null)} className="eden-btn-primary" style={{justifyContent:'center'}}>Chiudi</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── SEZIONE LOGS ──────────────────────────────────────────────────────────────
function TabLogs() {
  const navigate = useNavigate();
  const [data, setData]       = useState({ logs:[], total:0 });
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(0);
  const [filters, setFilters] = useState({ ruolo:'', azione:'', esito:'', utente_email:'', da:'', a:'' });
  const [detail, setDetail]   = useState(null);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit:LIMIT, offset:page*LIMIT });
      Object.entries(filters).forEach(([k,v]) => { if(v) params.append(k,v); });
      const [logs, s] = await Promise.all([
        api.get(`/logs?${params}`),
        stats===null ? api.get('/logs/stats') : Promise.resolve({data:stats}),
      ]);
      setData(logs.data);
      if(stats===null) setStats(s.data);
    } finally { setLoading(false); }
  }, [page, filters]);

  useEffect(()=>{ load(); },[load]);

  const resetFilters = () => { setFilters({ruolo:'',azione:'',esito:'',utente_email:'',da:'',a:''}); setPage(0); };

  const fmtTime = ts => {
    const d = new Date(ts);
    return d.toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit'}) + ' ' +
           d.toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit',second:'2-digit'});
  };

  const totalPages = Math.ceil(data.total / LIMIT);

  return (
    <div>
      {/* Stats strip */}
      {stats && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:20}}>
          {[
            {label:'Oggi',        value:stats.oggi,          color:'var(--cyan)'  },
            {label:'Settimana',   value:stats.settimana,     color:'var(--text)'  },
            {label:'Errori oggi', value:stats.errori_oggi,   color:'var(--red)'   },
            {label:'Azioni agenti',value:stats.azioni_agenti,color:'var(--purple)'},
            {label:'Azioni umani',value:stats.azioni_umani,  color:'var(--green)' },
          ].map(s=>(
            <div key={s.label} className="eden-card" style={{padding:'14px 18px'}}>
              <p style={{fontSize:11,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:4}}>{s.label}</p>
              <p style={{fontSize:24,fontWeight:700,color:s.color}}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="eden-card" style={{padding:'14px 16px',marginBottom:14,display:'flex',flexWrap:'wrap',gap:10,alignItems:'center'}}>
        <Filter size={14} color="var(--text-dim)"/>
        <input className="eden-input" value={filters.utente_email} style={{width:180}}
          onChange={e=>setFilters(f=>({...f,utente_email:e.target.value}))} placeholder="Email utente..."/>
        <select className="eden-select" value={filters.ruolo} onChange={e=>setFilters(f=>({...f,ruolo:e.target.value}))}>
          <option value="">Tutti i ruoli</option>
          <option value="admin">admin</option>
          <option value="direttore">direttore</option>
          <option value="operatore">operatore</option>
          <option value="agente">agente</option>
        </select>
        <input className="eden-input" value={filters.azione} style={{width:160}}
          onChange={e=>setFilters(f=>({...f,azione:e.target.value}))} placeholder="Azione (es. TASK)..."/>
        <select className="eden-select" value={filters.esito} onChange={e=>setFilters(f=>({...f,esito:e.target.value}))}>
          <option value="">Tutti gli esiti</option>
          <option value="ok">ok</option>
          <option value="errore">errore</option>
        </select>
        <input type="date" className="eden-input" value={filters.da} style={{width:140}}
          onChange={e=>setFilters(f=>({...f,da:e.target.value}))} title="Dal"/>
        <input type="date" className="eden-input" value={filters.a} style={{width:140}}
          onChange={e=>setFilters(f=>({...f,a:e.target.value}))} title="Al"/>
        <button onClick={resetFilters} className="eden-btn-ghost" style={{padding:'6px 10px'}}><X size={13}/> Reset</button>
        <button onClick={load} className="eden-btn-ghost" style={{padding:'6px 10px',marginLeft:'auto'}}>
          <RefreshCw size={13}/> Aggiorna
        </button>
      </div>

      {/* Table */}
      <div className="eden-card" style={{overflow:'hidden'}}>
        {loading && (
          <ListSkeleton rows={4} />
        )}
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)'}}>
                {['Quando','Cosa è successo','Esito'].map(h=>(
                  <th key={h} style={{textAlign:'left',padding:'10px 14px',fontSize:11,fontWeight:600,color:'var(--text-muted)',letterSpacing:'0.05em',textTransform:'uppercase',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.logs.length===0&&!loading&&(
                <tr><td colSpan={3} style={{textAlign:'center',padding:'40px',color:'var(--text-dim)',fontSize:13}}>Nessun log trovato</td></tr>
              )}
              {data.logs.map((log,i)=>{
                const A=AZIONE_ICON[log.azione]||{icon:Activity,color:'var(--text-muted)'};
                const isErr=log.esito==='errore';
                const {soggetto, azione} = logFrase(log);
                const RuoloIcon = RUOLO_STYLE[log.ruolo]?.icon || Activity;
                const ruoloColor = RUOLO_STYLE[log.ruolo]?.color || 'var(--text-dim)';
                const logLink = getLogLink(log);
                return (
                  <tr key={log.id}
                    onClick={()=>setDetail(log)}
                    style={{borderBottom:i<data.logs.length-1?'1px solid var(--border)':'none',cursor:'pointer',transition:'background 0.1s'}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--surface)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>

                    {/* Timestamp */}
                    <td style={{padding:'10px 14px',whiteSpace:'nowrap',verticalAlign:'top',width:130}}>
                      <div style={{fontSize:12,color:'var(--text-muted)',fontFamily:'monospace'}}>{fmtTime(log.creato_il)}</div>
                      {log.durata_ms!=null&&<div style={{fontSize:10,color:'var(--text-dim)',marginTop:2}}>{log.durata_ms}ms</div>}
                    </td>

                    {/* Frase leggibile */}
                    <td style={{padding:'10px 14px',verticalAlign:'top'}}>
                      <div style={{display:'flex',alignItems:'flex-start',gap:9}}>
                        <div style={{marginTop:1,flexShrink:0}}>
                          <A.icon size={14} color={A.color}/>
                        </div>
                        <div>
                          <span style={{fontSize:13,color:'var(--text)'}}>
                            <span style={{fontWeight:600,color:ruoloColor}}>{soggetto}</span>
                            {' '}{azione}
                          </span>
                          {isLogEsterno(log)&&(
                            <span style={{marginLeft:8,fontSize:10,padding:'1px 6px',borderRadius:999,
                              background:'rgba(168,85,247,0.15)',color:'var(--purple)',
                              border:'1px solid rgba(168,85,247,0.3)',fontWeight:600,verticalAlign:'middle'}}>
                              ESTERNO
                            </span>
                          )}
                          <div style={{display:'flex',alignItems:'center',gap:6,marginTop:3}}>
                            <RuoloIcon size={10} color={ruoloColor}/>
                            <span style={{fontSize:11,color:'var(--text-dim)'}}>{log.utente_email||'—'}</span>
                            {log.ip&&<span style={{fontSize:10,color:'var(--text-dim)',fontFamily:'monospace'}}> · {log.ip}</span>}
                            {logLink && (
                              <button
                                onClick={e => { e.stopPropagation(); navigate(logLink); }}
                                style={{
                                  background:'none', border:'1px solid var(--border)',
                                  borderRadius:5, color:'var(--cyan)', cursor:'pointer',
                                  padding:'1px 7px', fontSize:10.5, fontWeight:600,
                                  display:'flex', alignItems:'center', gap:3,
                                  marginLeft:4, flexShrink:0,
                                }}>
                                Apri →
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Esito */}
                    <td style={{padding:'10px 14px',verticalAlign:'top',whiteSpace:'nowrap'}}>
                      <span style={{fontSize:11,padding:'3px 10px',borderRadius:999,
                        background:isErr?'var(--red-dim)':'var(--green-dim)',
                        color:isErr?'var(--red)':'var(--green)',fontWeight:500,
                      }}>{isErr ? 'errore' : 'ok'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{padding:'10px 14px',borderTop:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span style={{fontSize:12,color:'var(--text-dim)'}}>{data.total} log totali</span>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} className="eden-btn-ghost" style={{padding:'5px 9px'}}>
              <ChevronLeft size={13}/>
            </button>
            <span style={{fontSize:12,color:'var(--text-muted)'}}>Pag. {page+1} / {Math.max(1,totalPages)}</span>
            <button onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page>=totalPages-1} className="eden-btn-ghost" style={{padding:'5px 9px'}}>
              <ChevronRight size={13}/>
            </button>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {detail&&(
        <div style={{position:'fixed',inset:0,zIndex:500,display:'flex',justifyContent:'flex-end'}}>
          <div onClick={()=>setDetail(null)} style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.4)',backdropFilter:'blur(2px)'}}/>
          <div style={{position:'relative',zIndex:1,width:420,background:'var(--bg2)',borderLeft:'1px solid var(--border)',padding:24,overflowY:'auto',boxShadow:'-6px 0 30px rgba(0,0,0,0.4)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
              <span style={{fontSize:14,fontWeight:600,color:'var(--text)'}}>Dettaglio log</span>
              <button onClick={()=>setDetail(null)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',display:'flex'}}><X size={16}/></button>
            </div>
            {isLogEsterno(detail)&&(
              <div style={{marginBottom:18,padding:'8px 12px',borderRadius:8,
                background:'rgba(168,85,247,0.08)',border:'1px solid rgba(168,85,247,0.25)',
                display:'flex',alignItems:'center',gap:8}}>
                <Activity size={13} color="var(--purple)"/>
                <span style={{fontSize:12,color:'var(--purple)',fontWeight:500}}>
                  Attivita esterna al CRM registrata dall agente
                </span>
              </div>
            )}
            {[
              ['Timestamp',  new Date(detail.creato_il).toLocaleString('it-IT')],
              ['Agente',     detail.ruolo==='agente'?detail.utente_email:null],
              ['Azione',     detail.azione],
              ['Risorsa / Sistema', detail.risorsa],
              ['ID risorsa', detail.risorsa_id],
              ['Metodo',     detail.metodo],
              ['Path',       detail.path],
              ['Utente',     detail.ruolo!=='agente'?detail.utente_email:null],
              ['Ruolo',      detail.ruolo],
              ['IP',         detail.ip],
              ['Status HTTP',detail.status_code],
              ['Esito',      detail.esito],
              ['Durata',     detail.durata_ms!=null?`${detail.durata_ms} ms`:null],
            ].filter(([,v])=>v!=null).map(([label,value])=>(
              <div key={label} style={{marginBottom:14}}>
                <p style={{fontSize:10.5,color:'var(--text-dim)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:3}}>{label}</p>
                <p style={{fontSize:13,color:'var(--text)',wordBreak:'break-all'}}>{String(value)}</p>
              </div>
            ))}
            {detail.payload&&(
              <div style={{marginBottom:14}}>
                <p style={{fontSize:10.5,color:'var(--text-dim)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>Payload</p>
                <pre style={{background:'#050510',border:'1px solid var(--border)',borderRadius:8,padding:'12px',fontSize:11.5,color:'var(--cyan)',overflowX:'auto',whiteSpace:'pre-wrap',wordBreak:'break-word'}}>
                  {JSON.stringify(typeof detail.payload==='string'?JSON.parse(detail.payload):detail.payload,null,2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── MAIN ADMIN PAGE ───────────────────────────────────────────────────────────
export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState('utenti');

  if(user?.ruolo!=='admin') return (
    <div style={{padding:32,textAlign:'center'}}>
      <p style={{color:'var(--red)',fontSize:14}}>Accesso riservato agli amministratori.</p>
    </div>
  );

  const TABS = [
    {id:'utenti', label:'Utenti',  icon:Users    },
    {id:'inviti', label:'Inviti',  icon:Mail     },
    {id:'logs',   label:'Log',     icon:Activity },
  ];

  return (
    <div className="eden-page">
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <h1 style={{fontSize:22,fontWeight:700,color:'var(--text)'}}>Pannello Admin</h1>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:4,marginBottom:22,background:'var(--surface)',borderRadius:10,padding:4,width:'fit-content'}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            display:'flex',alignItems:'center',gap:7,
            padding:'7px 18px',borderRadius:8,border:'none',cursor:'pointer',
            fontSize:13,fontWeight:500,transition:'all 0.15s',
            background:tab===t.id?'var(--bg2)':'transparent',
            color:tab===t.id?'var(--cyan)':'var(--text-muted)',
            boxShadow:tab===t.id?'var(--shadow)':'none',
          }}>
            <t.icon size={14}/>{t.label}
          </button>
        ))}
      </div>

      {tab==='utenti'&&<TabUtenti currentUser={user}/>}
      {tab==='inviti'&&<TabInviti/>}
      {tab==='logs'  &&<TabLogs/>}
    </div>
  );
}
